import { FC, SVGProps, useEffect, useRef, useState } from 'react'
import { ReactComponent as EditIcon } from 'src/assets/svg/edit-2.svg'
import { Skeleton } from 'src/components/Skeleton'
import { Tooltip } from 'src/components/Tooltip'
import { CampaignsOverviewKeywordWinner } from 'src/services/__generated__/api'
import styles from './positiveMentionsCard.module.scss'

// .keyword is min-height 32 / max-width 140 and .mentions is 14px
// (positiveMentionsCard.module.scss). Mirrors one .box of the card.
const BoxSkeleton: FC = () => (
  <>
    <Skeleton height={32} width={140} />
    <Skeleton height={14} width={72} />
  </>
)

interface TopKeywordCardProps {
  hasData?: boolean
  icon?: FC<SVGProps<SVGSVGElement>>
  isLoading?: boolean
  mostNegative?: CampaignsOverviewKeywordWinner
  mostPositive?: CampaignsOverviewKeywordWinner
  title?: string
  unitLabel?: string
}

export const TopKeywordCard: FC<TopKeywordCardProps> = ({
  hasData = false,
  icon: Icon = EditIcon,
  isLoading = false,
  mostNegative,
  mostPositive,
  title = 'Top Keyword',
  unitLabel = 'Mentions',
}) => {
  const positiveTextRef = useRef<HTMLDivElement>(null)
  const negativeTextRef = useRef<HTMLDivElement>(null)
  const [isPositiveTruncated, setIsPositiveTruncated] = useState(false)
  const [isNegativeTruncated, setIsNegativeTruncated] = useState(false)

  const positiveData =
    hasData && mostPositive && (mostPositive.positiveMentions || 0) > 0
      ? {
          mentions: mostPositive.positiveMentions || 0,
          text: mostPositive.keyword || 'No keyword',
        }
      : null
  const negativeData =
    hasData && mostNegative && (mostNegative.negativeMentions || 0) > 0
      ? {
          mentions: mostNegative.negativeMentions || 0,
          text: mostNegative.keyword || 'No keyword',
        }
      : null

  useEffect(() => {
    const checkTruncation = (element: HTMLDivElement | null) => {
      if (!element) return false
      return element.scrollHeight > element.clientHeight
    }

    setIsPositiveTruncated(checkTruncation(positiveTextRef.current))
    setIsNegativeTruncated(checkTruncation(negativeTextRef.current))
  }, [positiveData?.text, negativeData?.text])

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <Icon className={styles.icon} />
        <h3 className={styles.title}>{title}</h3>
      </div>
      <div className={styles.container}>
        <div className={styles.box}>
          <div className={styles.label}>
            <span className={styles.dot}></span>
            <span className={styles.labelText}>Most Positive</span>
          </div>
          {/* Loading first — "No Data" here was a claim about data that had
              not arrived yet. */}
          {isLoading ? (
            <BoxSkeleton />
          ) : positiveData ? (
            <>
              {isPositiveTruncated ? (
                <Tooltip
                  overlay={positiveData.text}
                  overlayClassName="topCampaignTooltip"
                  placement="top"
                >
                  <div className={styles.keyword} ref={positiveTextRef}>
                    {positiveData.text}
                  </div>
                </Tooltip>
              ) : (
                <div className={styles.keyword} ref={positiveTextRef}>
                  {positiveData.text}
                </div>
              )}
              <div className={styles.mentions}>
                {positiveData.mentions.toLocaleString()}{' '}
                {positiveData.mentions === 1
                  ? unitLabel.replace(/s$/, '')
                  : unitLabel}
              </div>
            </>
          ) : (
            <div className={styles.noData}>No Data</div>
          )}
        </div>
        <div className={styles.divider}></div>
        <div className={styles.box}>
          <div className={styles.label}>
            <span className={styles.dotNegative}></span>
            <span className={styles.labelText}>Most Negative</span>
          </div>
          {isLoading ? (
            <BoxSkeleton />
          ) : negativeData ? (
            <>
              {isNegativeTruncated ? (
                <Tooltip
                  overlay={negativeData.text}
                  overlayClassName="topCampaignTooltip"
                  placement="top"
                >
                  <div className={styles.keyword} ref={negativeTextRef}>
                    {negativeData.text}
                  </div>
                </Tooltip>
              ) : (
                <div className={styles.keyword} ref={negativeTextRef}>
                  {negativeData.text}
                </div>
              )}
              <div className={styles.mentions}>
                {negativeData.mentions.toLocaleString()}{' '}
                {negativeData.mentions === 1
                  ? unitLabel.replace(/s$/, '')
                  : unitLabel}
              </div>
            </>
          ) : (
            <div className={styles.noData}>No Data</div>
          )}
        </div>
      </div>
    </div>
  )
}
